import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Link from 'next/link';

const LocationBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const router = useRouter();

  useEffect(() => {
    const handleModalState = (event: CustomEvent) => {
      setIsModalOpen(event.detail);
    };

    document.addEventListener(
      'citiesModalOpen',
      handleModalState as EventListener
    );

    return () => {
      document.removeEventListener(
        'citiesModalOpen',
        handleModalState as EventListener
      );
    };
  }, []);

  useEffect(() => {
    let permissionStatus: PermissionStatus;

    const checkBannerVisibility = async () => {
      if (typeof window === 'undefined') return;

      const bannerDismissed =
        localStorage.getItem('bannerDismissed') === 'true';
      if (bannerDismissed) {
        setShowBanner(false);
        return;
      }

      const isExcludedPath =
        router.pathname.includes('/my-bookings/') ||
        router.pathname.includes('/confirmLater');

      if (isExcludedPath) {
        setShowBanner(false);
        return;
      }

      if ('permissions' in navigator) {
        try {
          permissionStatus = await navigator.permissions.query({
            name: 'geolocation'
          });

          const updateBanner = () => {
            setShowBanner(permissionStatus.state === 'granted');
          };

          updateBanner();
          permissionStatus.onchange = updateBanner;
        } catch (err) {
          console.warn('Permission query failed:', err);
        }
      }
    };

    checkBannerVisibility();

    return () => {
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, [router.pathname]);

  const handleClose = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('bannerDismissed', 'true');
    }
    setShowBanner(false);
  };

  if (!showBanner || isModalOpen) return null;

  return (
    <div className="w-full h-[70px] md:h-[66px] flex justify-center items-center bg-[#eef6f9] px-2">
      <p className="text-[#444444] text-[11px] xl:text-[14px] text-center w-full max-w-screen-lg">
        We use your location to display tour prices in your local currency. By
        continuing, you agree to our Privacy Policy. Read our{' '}
        <Link
          href="/privacy-policy"
          className="text-blue-600 underline hover:text-blue-800"
          target="_blank"
        >
          policy
        </Link>
        .
        <button
          onClick={handleClose}
          className="rounded-full border-2 p-1 ml-2 xl:ml-[4rem] align-middle inline-block"
        >
          <Image
            src="/close-icon.svg"
            alt="close-icon"
            width={20}
            height={20}
            className="h-2 w-2 xl:h-4 xl:w-4"
          />
        </button>
      </p>
    </div>
  );
};

export default LocationBanner;
